import React, { useState } from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Overview } from "@app/components/overview/overview";
import { ConfirmModal } from "@app/components/confirm-modal/confirm-modal";
import { getPlatformName } from "@app/util/platform-name";

import { DescriptionRunResultDTO, ResponseError } from "@app/api/generated";
import { ROUTES } from "@app/constants/routes";
import { runGeneration, setPlatformEnabled } from "@app/api/product-description-api";
import { DescriptionFilterBar } from "./components/description-filter-bar";
import { PlatformSidebar } from "./components/platform-sidebar";
import { PromptSettingsModal } from "./components/prompt-settings-modal";
import { RunsTable } from "./components/runs-table";
import { useDescriptionDashboard } from "./use-description-dashboard";
import styles from "@app/styles/dashboard/dashboard-layout.module.scss";

interface IPendingAction {
  title: string;
  message: string;
  run: () => Promise<void>;
}

const ProductDescription = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    platforms,
    runs,
    isLoading,
    currentPage,
    total,
    totalPages,
    statusFilter,
    platformFilter,
    anyRunning,
    refresh,
    setPlatforms,
    setCurrentPage,
    setStatusFilter,
    setPlatformFilter
  } = useDescriptionDashboard();

  const [pendingAction, setPendingAction] = useState<IPendingAction | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const markRunning = (platformId: number) => {
    setPlatforms((current) =>
      current.map((platform) => (platform.platformId === platformId ? { ...platform, isRunning: true } : platform))
    );
  };

  const doToggleEnabled = async (platformId: number, enabled: boolean) => {
    // Optimistic: flip immediately, then take the server's row.
    setPlatforms((current) =>
      current.map((platform) =>
        platform.platformId === platformId ? { ...platform, productDescriptionEnabled: enabled } : platform
      )
    );
    try {
      const updated = await setPlatformEnabled(platformId, enabled);
      setPlatforms((current) => current.map((platform) => (platform.platformId === platformId ? updated : platform)));
    } catch {
      toast.error(intl.formatMessage({ id: "productDescription.toast.toggleError" }));
      await refresh();
    }
  };

  /**
   * One platform per click. The ESB answers as soon as the run row exists and writes the batch on
   * its own time, so this only reports that the run started; the polling overview shows the rest.
   * A platform that is already running is refused with a 409.
   */
  const doGenerate = async (platformId: number) => {
    markRunning(platformId);
    try {
      await runGeneration(platformId);
      toast.success(intl.formatMessage({ id: "productDescription.toast.runStarted" }));
    } catch (error) {
      const alreadyRunning = (error as ResponseError)?.response?.status === 409;
      toast.error(
        intl.formatMessage({
          id: alreadyRunning ? "productDescription.toast.runInProgress" : "productDescription.toast.runError"
        })
      );
    } finally {
      await refresh();
    }
  };

  const requestGeneratePlatform = (platformId: number) => {
    setPendingAction({
      title: intl.formatMessage({ id: "productDescription.confirm.generate.title" }),
      message: intl.formatMessage(
        { id: "productDescription.confirm.generate.message" },
        { platform: getPlatformName(intl, platformId) }
      ),
      run: () => doGenerate(platformId)
    });
  };

  const requestToggleEnabled = (platformId: number, enabled: boolean) => {
    setPendingAction({
      title: intl.formatMessage({
        id: enabled ? "productDescription.confirm.activate.title" : "productDescription.confirm.deactivate.title"
      }),
      message: intl.formatMessage(
        {
          id: enabled ? "productDescription.confirm.activate.message" : "productDescription.confirm.deactivate.message"
        },
        { platform: getPlatformName(intl, platformId) }
      ),
      run: () => doToggleEnabled(platformId, enabled)
    });
  };

  const handleConfirm = async () => {
    if (!pendingAction) {
      return;
    }
    const { run } = pendingAction;
    // Close first; feedback comes from the optimistic RUNNING state, polling and toasts.
    setPendingAction(null);
    await run();
  };

  return (
    <div>
      <div className={clsx(styles.layout, styles.container)}>
        <Overview
          noContainer
          className={styles.tableColumn}
          title={intl.formatMessage({ id: "productDescription.title" }, { count: total })}
          actionBar={
            <DescriptionFilterBar
              platforms={platforms}
              statusFilter={statusFilter}
              platformFilter={platformFilter}
              onStatusChange={setStatusFilter}
              onPlatformChange={setPlatformFilter}
            />
          }
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          overviewItems={runs}
        >
          <RunsTable
            runs={runs}
            isLoading={isLoading}
            onRowClick={(run: DescriptionRunResultDTO) => navigate(`${ROUTES.PRODUCT_DESCRIPTION}/${run.runId}`)}
          />
        </Overview>

        <div className={styles.sidebar}>
          <PlatformSidebar
            platforms={platforms}
            anyRunning={anyRunning}
            onGeneratePlatform={requestGeneratePlatform}
            onToggleEnabled={requestToggleEnabled}
            onOpenSettings={() => setIsSettingsOpen(true)}
          />
        </div>
      </div>

      <PromptSettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />

      <ConfirmModal
        isOpen={pendingAction !== null}
        title={pendingAction?.title ?? ""}
        message={pendingAction?.message ?? ""}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
};

export { ProductDescription };
